import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { MenuProps } from "antd";
import {
  ApartmentOutlined,
  AppstoreAddOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  CloudSyncOutlined,
  MonitorOutlined,
  NotificationOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useMatch } from "react-router";
import { MenuItemType } from "antd/lib/menu/interface";

import { Sidebar, useApplicationItems } from "./lib/ui-components/layout/sidebar";
import { useUser } from "./lib/contexts/user";
import { useFeature } from "./lib/contexts/feature-flags";
import { addIf } from "./lib/util";
import { NewFeatureIndicator } from "./lib/ui-components/new-feature-indicator";

export function EmsLayout({ children }: React.PropsWithChildren) {
  const applicationItems = useApplicationItems();
  return (
    <div className="grid h-dvh grid-cols-[auto_1fr] grid-rows-[auto_1fr]">
      <EmsSidebar applicationItems={applicationItems} selectedApplicationKey="ems" />
      {children ?? <Outlet />}
    </div>
  );
}

export function EmsSidebar({
  applicationItems,
  selectedApplicationKey,
}: {
  applicationItems: Array<MenuItemType>;
  selectedApplicationKey: string;
}) {
  const selectedKey = useMatch(":orgaId/:root/*")?.params.root ?? "";

  const { organizations } = useUser();
  const canViewDataSources = useFeature("permissions/data-sources-overview");
  const showMultisite = organizations.filter((orga) => orga.edgeDevices.length > 0).length > 1;

  const emsMenuItems: MenuProps["items"] = [
    {
      key: "energy-overview",
      label: (
        <Link to="energy-overview">
          <Trans>Energy Overview</Trans>
        </Link>
      ),
      icon: <AppstoreAddOutlined aria-hidden />,
    },
    { type: "divider" },
    {
      key: "plan",
      label: (
        <Link to="plan">
          <Trans>Measuring Concept</Trans>
        </Link>
      ),
      icon: <ApartmentOutlined aria-hidden />,
    },
    ...addIf(canViewDataSources, [
      {
        key: "data-sources",
        label: (
          <Link to="data-sources">
            <Trans>Data Sources</Trans>
          </Link>
        ),
        icon: <CloudSyncOutlined aria-hidden />,
      },
    ]),
    { type: "divider" },
    {
      key: "monitoring",
      label: t`Monitoring`,
      icon: <BarChartOutlined aria-hidden />,
      children: [
        {
          key: "consumption",
          label: (
            <Link to="consumption">
              <Trans>Consumption</Trans>
            </Link>
          ),
        },
        {
          key: "trend",
          label: (
            <Link to="trend">
              <Trans>Trend</Trans>
            </Link>
          ),
        },
        {
          key: "energy-flow",
          label: (
            <Link to="energy-flow">
              <Trans>Energy Flow</Trans>
            </Link>
          ),
        },
        ...addIf(showMultisite, [
          {
            key: "multisite",
            label: (
              <Link to="multisite">
                <Trans>Multisite</Trans>
              </Link>
            ),
          },
        ]),
      ],
    },
    {
      key: "analysis",
      label: t`Analysis`,
      icon: <MonitorOutlined aria-hidden />,
      children: [
        {
          key: "operating-states",
          label: (
            <Link to="operating-states">
              <Trans>Operating States</Trans>
            </Link>
          ),
        },
        {
          key: "utilization",
          label: (
            <Link to="utilization">
              <Trans>Utilization</Trans>
            </Link>
          ),
        },
        {
          key: "peak-load",
          label: (
            <Link to="peak-load">
              <Trans>Peak Loads</Trans>
            </Link>
          ),
        },
        {
          key: "energy-prices",
          label: (
            <Link to="energy-prices">
              <Trans>Energy Prices</Trans>
            </Link>
          ),
        },
      ],
    },
    {
      key: "management",
      label: t`Management`,
      icon: <ScheduleOutlined />,
      children: [
        {
          key: "area",
          label: (
            <NewFeatureIndicator featureId="area-page" showUntil={new Date("2026-11-14")}>
              <Link to="area">
                <Trans>Areas</Trans>
              </Link>
            </NewFeatureIndicator>
          ),
        },
        {
          key: "action-plan",
          label: (
            <Link to="action-plan">
              <Trans>Actions</Trans>
            </Link>
          ),
        },
        {
          key: "reports",
          label: (
            <Link to="reports">
              <Trans>Reports</Trans>
            </Link>
          ),
        },
      ],
    },
    { type: "divider" },
    {
      key: "agent",
      label: <Link to="agent">agent Login</Link>,
      icon: <ArrowRightOutlined aria-hidden />,
    },
    { type: "divider" },
    {
      key: "release-notes",
      label: (
        <NewFeatureIndicator featureId="release-notes-26-10" showUntil={new Date("2026-11-17")}>
          <a
            href="https://docs.proalpha.com/gatekeeper/guesttoken/717?app=%2Fservice-express%2Fportal%2Fobject%2Fesc%2Fde-DE___bundle___release_notes_enit___page___release_notes.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Release Notes
          </a>
        </NewFeatureIndicator>
      ),
      icon: <NotificationOutlined aria-hidden />,
    },
  ];

  return (
    <Sidebar
      navigationItems={emsMenuItems}
      selectedNavigationKey={selectedKey}
      applicationItems={applicationItems}
      selectedApplicationKey={selectedApplicationKey}
    />
  );
}
